import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useDeclineTradeOfferMutation } from 'store/services/tradeOffers/tradeOffersApi';
import { TradeOffer } from 'types/interfaces/tradeOffer';

/** The undo window. The timer is the authority — dismissing the toast never undoes. */
export const DECLINE_UNDO_MS = 8000;

export interface DeferredDeclineApi {
  /** True while an offer is optimistically declined but the request hasn't fired */
  isLocallyDeclined: (offerId: string) => boolean;
  /** One-tap decline: mark locally, toast with Undo, send the request when the window closes */
  declineWithUndo: (offer: TradeOffer) => void;
  /** Server state wins: drop marks (and pending timers) for offers no longer pending */
  reconcile: (offers: TradeOffer[]) => void;
}

const toastId = (offerId: string) => `offer-decline-${offerId}`;

/**
 * Deferred-send decline. No reopen endpoint exists, so the decline request is
 * held for DECLINE_UNDO_MS and only sent if the user doesn't undo; undo inside
 * the window means no request was ever made. Owned by TradeOffersPage so the
 * marks survive row re-renders. Unmounting (in-app navigation) flushes pending
 * declines immediately so the intent is never lost; closing the tab inside the
 * window loses the decline, which fails safe — the offer simply stays pending.
 */
export const useDeferredDecline = (): DeferredDeclineApi => {
  const [declineOffer] = useDeclineTradeOfferMutation();
  const [declinedIds, setDeclinedIds] = useState<Set<string>>(() => new Set());
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // The flush-on-unmount effect must see the latest trigger without re-running
  const declineRef = useRef(declineOffer);
  declineRef.current = declineOffer;

  const unmark = useCallback((offerId: string) => {
    setDeclinedIds((prev) => {
      if (!prev.has(offerId)) return prev;
      const next = new Set(prev);
      next.delete(offerId);
      return next;
    });
  }, []);

  const fireDecline = useCallback(
    (offerId: string) => {
      timersRef.current.delete(offerId);
      declineRef
        .current(offerId)
        .unwrap()
        .catch(() => {
          // The offer expired or was cancelled mid-window; the mutation's tag
          // invalidation refetches and the row lands on its true status.
          unmark(offerId);
        });
    },
    [unmark]
  );

  const undo = useCallback(
    (offerId: string) => {
      const timer = timersRef.current.get(offerId);
      if (timer) {
        clearTimeout(timer);
        timersRef.current.delete(offerId);
      }
      unmark(offerId);
      toast.dismiss(toastId(offerId));
    },
    [unmark]
  );

  const declineWithUndo = useCallback(
    (offer: TradeOffer) => {
      if (timersRef.current.has(offer.id)) return;
      setDeclinedIds((prev) => new Set(prev).add(offer.id));
      timersRef.current.set(
        offer.id,
        setTimeout(() => fireDecline(offer.id), DECLINE_UNDO_MS)
      );
      toast.info(
        <span className="offer-decline-toast">
          Offer declined.{' '}
          <button
            type="button"
            className="offer-decline-undo-btn"
            onClick={(e) => {
              e.stopPropagation();
              undo(offer.id);
            }}
          >
            Undo
          </button>
        </span>,
        { toastId: toastId(offer.id), autoClose: DECLINE_UNDO_MS }
      );
    },
    [fireDecline, undo]
  );

  const isLocallyDeclined = useCallback(
    (offerId: string) => declinedIds.has(offerId),
    [declinedIds]
  );

  const reconcile = useCallback(
    (offers: TradeOffer[]) => {
      for (const offer of offers) {
        if (offer.status !== 'pending' && timersRef.current.has(offer.id)) {
          // A socket refetch says the offer already reached a terminal state:
          // don't fire a pointless decline, and show the server's truth.
          const timer = timersRef.current.get(offer.id);
          if (timer) clearTimeout(timer);
          timersRef.current.delete(offer.id);
          unmark(offer.id);
          toast.dismiss(toastId(offer.id));
        }
      }
    },
    [unmark]
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      // In-app navigation away flushes pending declines so intent isn't lost
      timers.forEach((timer, offerId) => {
        clearTimeout(timer);
        declineRef.current(offerId);
      });
      timers.clear();
    };
  }, []);

  return { isLocallyDeclined, declineWithUndo, reconcile };
};

export default useDeferredDecline;
